import React, { useEffect, useState } from "react";
import "./TuitionClasses.scss";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { useDispatch, useSelector } from "react-redux";
import { startLoading, stopLoading } from "../../redux/slices/WindowSlice";
import { get } from "../../redux/ApiHelper";
import { TuitionClassInterface } from "../../interfaces/tuitionClass.interface";
import { TextField } from "@mui/material";
import { Column } from "../../interfaces/table.interface";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TablePagination from "@mui/material/TablePagination";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import { RootState } from "../../redux/store";



const TuitionClasses: React.FC = () => {
  const dispatch = useDispatch();
  const [value, setValue] = useState("all");
  const { signedAdmin } = useSelector((state: RootState) => state.admin);
  const [dataFetched, setDataFetched] = useState<Record<string, boolean>>({
    all: false,
    ongoing: false,
    today: false,
    upcoming: false,
    history: false,
  });
  const [classes, setClasses] = useState<
    Record<string, TuitionClassInterface[]>
  >({
    all: [],
    ongoing: [],
    today: [],
    upcoming: [],
    history: [],
  });

  const [filter, setFilter] = useState(""); // Search input (Teacher, Student, Parent)
  const [filteredClasses, setFilteredClasses] =
    useState<Record<string, TuitionClassInterface[]>>(classes); // Filtered classes after applying date or search filters
  const [selectedDate, setSelectedDate] = useState<string | null>(null); // Selected date from Date Picker
  const [alltuitionClassNumber, setAllTuitionClassesNumber] = useState(0);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
    setPage(1); // Reset to first page when changing tabs
    fetchClasses(newValue, 1, rowsPerPage);
  };

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(page);
    fetchClasses(value, page, newRowsPerPage);
  };
  // Fetch classes from API
  const fetchClasses = (
    tab: string,
    pageParam = page,
    limitParam = rowsPerPage
  ) => {
    dispatch(startLoading());
    const queryParams = new URLSearchParams();
    queryParams.set("page", String(pageParam));
    queryParams.set("limit", String(limitParam));
    queryParams.set("type", String(signedAdmin.type));
    queryParams.set("adminId", String(signedAdmin.id));
    const apiUrl = `/admin/tuition/schedules?${tab !== "all" ? `filter=${tab}&` : ""
      }${queryParams.toString()}`;

    get(apiUrl)
      .then((response) => {
        setClasses((prevClasses) => ({
          ...prevClasses,
          [tab]: response.tuitionClasses,
        }));
        setFilteredClasses((prevClasses) => ({
          ...prevClasses,
          [tab]: response.tuitionClasses,
        }));
        setAllTuitionClassesNumber(response.totalResults);
        setDataFetched((prevFetched) => ({
          ...prevFetched,
          [tab]: true,
        }));
      })
      .catch((error) => console.error(`Error fetching ${tab} data:`, error))
      .finally(() => dispatch(stopLoading()));
  };

  useEffect(() => {
    fetchClasses("all"); // Fetch default tab data on load
    // eslint-disable-next-line
  }, []);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value;
    setFilter(searchTerm);
    filterClasses(searchTerm, selectedDate);
  };

  // Function to handle Date Picker change
  const handleDateChange = (newDate: string | null) => {
    setSelectedDate(newDate);
    filterClasses(filter, newDate);
  };

  const filterClasses = (searchTerm: string, dateFilter: string | null) => {
    const filtered =
      classes[value]?.filter((classData) => {
        // 1) Search‐term match (or always‐true if searchTerm is blank)
        const matchesSearch = searchTerm.trim()
          ? classData.tuitionPeriod?.teacher?.name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          classData.tuitionPeriod?.student?.name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          classData.tuitionPeriod?.parent?.name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          classData.tuitionPeriod?.teacher?._id
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          classData.tuitionPeriod?.student?._id
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          classData.tuitionPeriod?.parent?._id
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
          : true;

        // 2) Date match (compare YYYY-MM-DD to the start of your ISO timestamp)
        const scheduleDate = classData.tuitionClassSchedule?.schedule?.date;
        const classDate = scheduleDate
          ? (typeof scheduleDate === 'string'
            ? scheduleDate.slice(0, 10)
            : new Date(scheduleDate).toISOString().slice(0, 10))
          : null;
        const matchesDate = dateFilter ? classDate === dateFilter : true;

        return matchesSearch && matchesDate;
      }) ?? [];

    // Use a functional update to avoid stale state
    setFilteredClasses((prev) => ({
      ...prev,
      [value]: filtered,
    }));
  };

  useEffect(() => {
    filterClasses(filter, selectedDate); // Re-filter classes when value or selectedDate changes
    // eslint-disable-next-line
  }, [selectedDate, filter]);

  const columns: Column[] = [
    // { select: "securedCoins", header: "Secured coins" },
    {
      header: "Parent name",
      render: (_value: any, row: TuitionClassInterface) =>
        row?.tuitionPeriod?.parent?.name || "N/A",
    },
    {
      header: "Student name",
      render: (_value: any, row: TuitionClassInterface) =>
        row?.tuitionPeriod?.student?.name || "N/A",
    },
    {
      header: "Teacher name",
      render: (_value: any, row: TuitionClassInterface) =>
        row?.tuitionPeriod?.teacher?.name || "N/A",
    },
    {
      header: "CreatedAt",
      render: (_value: any, row: TuitionClassInterface) =>
        new Date(row?.createdAt).toLocaleString() || "N/A",
    },
    {
      header: "Teacher action status",
      render: (_value: any, row: TuitionClassInterface) => {
        return (
          <div className="flex items-center">
            {row.teacherAttendanceRequest && (
              <p className="mt-1">
                Attendance Status:{" "}
                <span
                  className={
                    row.isTeacherPresent ? "text-green-500" : "text-red-500"
                  }
                >
                  {row.isTeacherPresent ? "Present" : "Not Present"}
                </span>
              </p>
            )}
          </div>
        );
      },
    },
    {
      header: "Student Attendance status",
      render: (_value: any, row: TuitionClassInterface) => {
        return (
          <div className="flex items-center">
            {row.teacherAttendanceRequest && (
              <p className="mt-1">
                Attendance Status:{" "}
                <span
                  className={
                    row.isStudentPresent ? "text-green-500" : "text-red-500"
                  }
                >
                  {row.isStudentPresent ? "Present" : "Not Present"}
                </span>
              </p>
            )}
          </div>
        );
      },
    },
    {
      header: "Details",
      render: (_value: any, row: TuitionClassInterface) => {
        return (
          <div className="flex">
            <div className="flex flex-col justify-center items-center min-w-[150px]">
              <h2 className="">
                Class on{" "}
                {row?.tuitionClassSchedule?.schedule?.date
                  ? new Date(
                    row?.tuitionClassSchedule?.schedule?.date
                  )?.toLocaleDateString("en-IN")
                  : "Not updated yet"}
              </h2>
              {row?.tuitionClassSchedule?.schedule?.from && (
                <p className="text-gray-600 mt-2">
                  Time: {row.tuitionClassSchedule.schedule.from} -{" "}
                  {row.tuitionClassSchedule.schedule.to}
                </p>
              )}

              {row?.tuitionPeriod?.subjects?.length > 0 && (
                <p className="text-gray-600">
                  Subject(s): {row?.tuitionPeriod?.subjects?.join(", ")}
                </p>
              )}
            </div>
            <div className="mt-3 flex flex-col md:flex-row md:justify-around gap-4 min-w-[400px]">
              <div className="mb-4 md:mb-0">
                <p className="text-center font-semibold my-2">
                  Teacher Attendance Request
                </p>
                <p className="text-gray-600 text-left">
                  <span className="text-blue-700 mr-2">Status:</span>
                  <span
                    className={
                      "ml-2 " +
                      (row?.teacherAttendanceRequest?.status === "pending"
                        ? "text-yellow-500"
                        : row?.teacherAttendanceRequest?.status === "approved"
                          ? "text-green-500"
                          : "text-red-500")
                    }
                  >
                    {row?.teacherAttendanceRequest?.status?.toUpperCase() ||
                      "Not Available"}
                  </span>
                </p>
                <p className="text-gray-600 text-left">
                  <span className="text-blue-700 mr-2">Request Time :</span>
                  <span>
                    {row?.teacherAttendanceRequest?.requestTime ? (
                      new Date(
                        row?.teacherAttendanceRequest?.requestTime
                      ).toLocaleString("en-IN", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    ) : (
                      <span className="text-red-500">Not Available</span>
                    )}
                  </span>
                </p>
                {row?.teacherAttendanceRequest?.approveOrRejectTime && (
                  <p className="text-gray-600 text-left">
                    {row?.teacherAttendanceRequest?.status === "approved"
                      ? "Approval"
                      : "Rejection"}{" "}
                    <span className="text-blue-700 mr-2">Time :</span>
                    <span>
                      {row?.teacherAttendanceRequest?.approveOrRejectTime &&
                        new Date(
                          row?.teacherAttendanceRequest?.approveOrRejectTime
                        ).toLocaleString("en-IN", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                    </span>
                  </p>
                )}
              </div>

              <div className="mb-4 md:mb-0">
                <p className="text-center font-semibold my-2">
                  Student Attendance Request
                </p>
                <p className="text-gray-600 text-left">
                  <span className="text-blue-700 mr-2">Status:</span>
                  <span
                    className={
                      row?.studentAttendanceRequest?.status === "pending"
                        ? "text-yellow-500"
                        : row?.studentAttendanceRequest?.status === "approved"
                          ? "text-green-500"
                          : "text-red-500"
                    }
                  >
                    {row?.studentAttendanceRequest?.status?.toUpperCase() ||
                      "Not Available"}
                  </span>
                </p>
                <p className="text-gray-600 text-left">
                  <span className="text-blue-700 mr-2">Request Time :</span>
                  <span>
                    {row?.studentAttendanceRequest?.requestTime ? (
                      new Date(
                        row?.studentAttendanceRequest?.requestTime
                      ).toLocaleString("en-IN", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    ) : (
                      <span className="text-red-500">Not Available</span>
                    )}
                  </span>
                </p>
                {row?.studentAttendanceRequest?.approveOrRejectTime && (
                  <p className="text-gray-600 text-left">
                    {row?.studentAttendanceRequest?.status === "approved"
                      ? "Approval"
                      : "Rejection"}{" "}
                    <span className="text-blue-700 mr-2">Time :</span>
                    <span>
                      {row?.studentAttendanceRequest?.approveOrRejectTime &&
                        new Date(
                          row?.studentAttendanceRequest?.approveOrRejectTime
                        ).toLocaleString("en-IN", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                    </span>
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    // { select: "meetingLink", header: "Meeting link" },
  ];

  return (
    <div className="max-w-[100vw] overflow-x-hidden">
      <div className="title text-xl text-yellow-500">Tuition Classes</div>
      <div className="container flex flex-col flex-grow overflow-hidden">
        <TabContext value={value}>
          <Box
            className="mb-3"
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <TabList
              onChange={handleTabChange}
              aria-label="Tuition Classes Tabs"
              sx={{
                "& .MuiTab-root": {
                  color: "var(--color-light-gray)",
                  "&:hover": {
                    color: "var(--color-primary)!important",
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                    borderRadius: "8px 8px 0 0",
                  },
                },
                "& .Mui-selected": {
                  color: "var(--color-primary)!important",
                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                  fontWeight: "bold",
                  borderRadius: "8px 8px 0 0",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "var(--color-primary)",
                },
              }}
            >
              <Tab label="All" value="all" />
              <Tab label="Ongoing" value="ongoing" />
              <Tab label="Today" value="today" />
              <Tab label="Upcoming" value="upcoming" />
              <Tab label="History" value="history" />
            </TabList>
          </Box>
          <div className="flex gap-4">
            <div className="search-bar mb-3 p-4 bg-gray-200 flex-grow">
              <input
                type="text"
                placeholder="Search by Teacher, Student, or Parent..."
                value={filter}
                onChange={handleSearchChange}
                className="p-2 w-full rounded-md border border-gray-300"
              />
            </div>
            <div className="date-filter mb-3 p-4 bg-gray-200 flex-grow">
              <TextField
                type="date"
                value={selectedDate}
                onChange={(e) => handleDateChange(e.target.value)}
                className="w-full p-2 rounded-md"
              />
            </div>
            <div className="reset-button mb-3 p-4 flex items-center">
              <button
                onClick={() => {
                  setFilter(""); // Clear search term
                  setSelectedDate(null); // Clear selected date
                  filterClasses("", null); // Reset filter
                }}
                className="p-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition duration-300"
              >
                Reset
              </button>
            </div>
          </div>
        </TabContext>
      </div>

      <div className="">
        <TabContext value={value}>
          {["all", "ongoing", "today", "upcoming", "history"].map(
            (item, index) => (
              <TabPanel value={item} key={index}>
                <TableContainer>
                  <div className="overflow-x-auto max-w-[100vw]">
                    <div className="table-component-container">
                      <TableContainer component={Paper} className="">
                        <Table
                          sx={{ minWidth: 700 }}
                          aria-label="customized table"
                        >
                          <TableHead>
                            <TableRow>
                              {columns?.map((column, index) => (
                                <StyledTableCell
                                  key={index}
                                  style={{
                                    textAlign: column?.align || "center",
                                    padding: "4px 16px",
                                  }}
                                >
                                  {column.header}
                                </StyledTableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredClasses[item]?.length === 0 ? (
                              <StyledTableRow>
                                <StyledTableCell
                                  colSpan={columns?.length}
                                  style={{ textAlign: "center" }}
                                >
                                  No Data Available
                                </StyledTableCell>
                              </StyledTableRow>
                            ) : (
                              filteredClasses[item].map((row, rowIndex) => (
                                <StyledTableRow key={rowIndex}>
                                  {columns?.map((column, colIndex) => (
                                    <StyledTableCell
                                      key={colIndex}
                                      style={{
                                        textAlign: column?.align || "center",
                                        padding: "8px 16px",
                                      }}
                                    >
                                      {column?.render
                                        ? column?.render(
                                          row[column.select],
                                          row
                                        )
                                        : row[column.select]}
                                    </StyledTableCell>
                                  ))}
                                </StyledTableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={alltuitionClassNumber}
                        page={page - 1}
                        onPageChange={(e, newPage) => {
                          setPage(newPage + 1);
                          fetchClasses(value, newPage + 1, rowsPerPage);
                        }}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[15, 30, 50]}
                        labelDisplayedRows={({ from, to, count }) => {
                          const adjustedTo = Math.min(to, count); // Ensure 'to' does not exceed count
                          return `${from}–${adjustedTo} of ${count}`;
                        }}
                        className="table-pagination min-h-10"
                      />
                    </div>
                  </div>
                </TableContainer>
              </TabPanel>
            )
          )}
        </TabContext>
      </div>
    </div>
  );
};

export default TuitionClasses;

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: "var(--color-primary)",
    padding: 4,
    textAlign: "center",
    position: "sticky",
    top: 0,
    zIndex: 1, // Ensure it is on top of the body
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: 4,
    textAlign: "center",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));
